/****************************************************************************************
 * File Name    : FilterWorkRegister.vue
 * Function     : 필터 일일 작업일지 - 신규등록
 * Author       : 
 * Description  : 일일 작업일지 템플릿 생성 및 관리
 * Modification Log
 * ======================================================================================
 * Ver  Date        	Author     	Modification
 * ======================================================================================
   1.0  2026.02.17  	        	Create
****************************************************************************************/
<template>
    <div class="">
        <div v-if="flagSpinner === false">
            <a-row :gutter="16">
                <a-col class="gutter-row" :span="24">
                    <div class="gutter-box">
                        <PageHeader title="필터 일일 작업일지 - 신규등록" />
                    </div>
                </a-col>
            </a-row>
            <a-divider type='horizontal' />
            
            <a-space align="end" style="padding-top:10px;">
                <a-radio-group v-model="filterState">
                    <a-radio-button @click="changeFilterState('등록중')" value="등록중">등록중만 보기</a-radio-button>
                    <a-radio-button @click="changeFilterState('등록완료')" value="등록완료">등록완료만 보기</a-radio-button>
                    <a-radio-button @click="changeFilterState('모두')" value="모두">모두보기</a-radio-button>
                </a-radio-group>
                <a-input placeholder="검색할 이름을 입력하세요" v-model="filterCS" />
                <a-button type="primary" ghost="true" @click="filterCSList">검색</a-button>
                <a-button @click="resetCSList">초기화</a-button>
                <a-divider type='vertical' />
                <div class="border:1px solid red;width:100%">
                    <a-button type="primary" icon="plus-circle" size="default" @click="showCreateModal">
                        일일 작업일지 템플릿 생성
                    </a-button>
                </div>
            </a-space>
            <p/>
            <a-progress type='line' :percent="validRate" strokeColor='#C7C6C6'/>
            <p/>
            
            <a-table :columns="columns" size="small" :bordered="false" :data-source="data" :pagination="pagination" :scroll="{ x: 0, y: '57vh' }" @change="handleTableChange">
                <span slot="tags" slot-scope="tag">
                    <a-tag class="state-btn" :class="{ red: (tag === '등록중'), green: (tag === '등록완료') }">
                        {{ tag.toUpperCase() }}
                    </a-tag>
                </span>
                <span slot="name" slot-scope="text">{{ text }}</span>
                <span slot="action" slot-scope="record">
                    <span style='cursor:pointer' @click="handlerToEdit(record.key)"><a-icon type="edit" /> Edit</span>
                    <a-divider type="vertical" />
                    <span style='cursor:pointer' @click="handlerToCopy(record.key)"><a-icon type="copy" /> 복사생성</span>
                    <a-divider type="vertical" />
                    <span style='cursor:pointer' @click="handlerToDelete(record.key)"><a-icon type="delete" /> Delete</span>
                    <a-divider type="vertical" />
                    <span style='cursor:pointer' @click="handlerToView(record.key)"><a-icon type="eye" /> View</span>
                </span>
            </a-table>
        </div>
        <a-skeleton v-else />

        <!-- 템플릿 생성/수정 Modal -->
        <a-modal
            v-model="isCreateModalVisible"
            :title="modalTitle"
            width="90%"
            :footer="null"
            @cancel="handleModalCancel"
        >
            <a-form :form="form" layout="vertical">
                <!-- 기본정보 -->
                <a-card title="기본정보" :bordered="false" style="margin-bottom: 16px;">
                    <a-row :gutter="16">
                        <a-col :span="6">
                            <a-form-item label="템플릿명">
                                <a-input v-model="formData.basicInfo.templateName" placeholder="템플릿명" :disabled="viewMode" />
                            </a-form-item>
                        </a-col>
                        <a-col :span="6">
                            <a-form-item label="라인명">
                                <a-input v-model="formData.basicInfo.lineName" placeholder="라인명" :disabled="viewMode" />
                            </a-form-item>
                        </a-col>
                        <a-col :span="6">
                            <a-form-item label="작성자">
                                <a-input v-model="formData.basicInfo.writer" placeholder="작성자" :disabled="viewMode" />
                            </a-form-item>
                        </a-col>
                        <a-col :span="6">
                            <a-form-item label="생산 모델">
                                <a-select
                                    v-model="formData.basicInfo.productSeq"
                                    show-search
                                    placeholder="모델명 또는 제품명으로 검색"
                                    :filter-option="filterProductOption"
                                    style="width: 100%"
                                    allow-clear
                                    option-label-prop="label"
                                    :disabled="viewMode"
                                >
                                    <a-select-option
                                        v-for="p in productList"
                                        :key="p.productSeq"
                                        :value="p.productSeq"
                                        :label="`${p.productModel} (${p.productName})`"
                                    >
                                        {{ p.productModel }} ({{ p.productName }})
                                    </a-select-option>
                                </a-select>
                            </a-form-item>
                        </a-col>
                    </a-row>
                </a-card>

                <!-- 일지 양식 미리보기 (관리자 입력 가능 항목은 입력 필드, 나머지는 고정 표시) -->
                <a-card :bordered="false" style="margin-bottom: 16px;">
                    <a-alert message="노란색 배경 항목만 템플릿마다 다르게 입력할 수 있습니다. 나머지는 고정 양식입니다." type="info" show-icon style="margin-bottom:12px;" />

                    <!-- 제목 헤더 -->
                    <div class="prev-header">
                        <div class="prev-title">
                            <a-input v-model="formData.templateTexts.managerInput" placeholder="관리자 입력 텍스트" :disabled="viewMode" class="prev-input-inline prev-input-highlight" style="width:200px;" /> 작업일보
                        </div>
                        <div class="prev-fixed-text">년 &nbsp;&nbsp; 월 &nbsp;&nbsp; 일 &nbsp;&nbsp; 요일</div>
                    </div>

                    <!-- 2. 자주보전 -->
                    <div class="prev-section-title">2. 자주보전</div>
                    <div style="display:flex;gap:12px;margin-bottom:8px;">
                        <div style="flex:1;">
                            <div class="prev-subsection-title">실내 온도/습도</div>
                            <table class="prev-table">
                                <thead><tr><th></th><th>오전 1</th><th>오전 2</th><th>오후 1</th><th>오후 2</th></tr></thead>
                                <tbody>
                                    <tr><td class="prev-label">℃</td><td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td></tr>
                                    <tr><td class="prev-label">%</td><td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td></tr>
                                </tbody>
                            </table>
                            <div class="prev-subsection-title" style="margin-top:6px;">설비보전내용</div>
                            <div class="prev-empty-area"></div>
                        </div>
                        <div style="flex:1;">
                            <div class="prev-subsection-title">혼합기 원료 투입량 체크</div>
                            <table class="prev-table">
                                <thead><tr><th>구분</th><th>활성탄 투입량</th><th>바인디 투입량</th></tr></thead>
                                <tbody>
                                    <tr v-for="label in ['오전 1','오전 2','오후 1','오후 2']" :key="label">
                                        <td class="prev-label">{{ label }}</td>
                                        <td class="prev-empty">kg</td>
                                        <td class="prev-empty">kg</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- 3. 성능평가 -->
                    <div class="prev-section-title">3. 성능평가</div>
                    <table class="prev-table">
                        <thead>
                            <tr>
                                <th rowspan="2"></th>
                                <th colspan="3">강도 평가</th>
                                <th colspan="3">중량 평가</th>
                                <th colspan="4">길이 평가</th>
                            </tr>
                            <tr>
                                <th>1호기</th><th>2호기</th><th>3호기</th>
                                <th>1호기</th><th>2호기</th><th>3호기</th>
                                <th>1호기</th><th>2호기</th><th>3호기</th><th>특이사항</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="label in ['오전 1','오전 2','오후 1','오후 2']" :key="label">
                                <td class="prev-label">{{ label }}</td>
                                <td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td>
                                <td class="prev-empty">g</td><td class="prev-empty">g</td><td class="prev-empty">g</td>
                                <td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- 4. 탁도/잔류염소 -->
                    <div class="prev-section-title">4. 성능평가 (탁도/잔류염소 평가)</div>
                    <div style="display:flex;gap:12px;">
                        <!-- 오전 -->
                        <div style="flex:1;">
                            <div class="prev-subsection-title">오전</div>
                            <div class="prev-note-editable">
                                <a-input v-model="formData.templateTexts.turbidityNote" :disabled="viewMode" class="prev-input-highlight" size="small" />
                            </div>
                            <table class="prev-table">
                                <thead>
                                    <tr><th>모델</th><th>평가자</th><th>#1탁도</th><th>#1유량</th><th>#2탁도</th><th>#2유량</th><th>#3탁도</th><th>#3유량</th><th>특이사항</th></tr>
                                </thead>
                                <tbody>
                                    <tr><td class="prev-empty" colspan="9" style="height:28px;"></td></tr>
                                    <tr>
                                        <td class="prev-label" colspan="2">원수탁도 / 시간</td>
                                        <td class="prev-empty" colspan="7"></td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="prev-note-editable" style="margin-top:6px;">
                                <a-input v-model="formData.templateTexts.concentrationFormula" :disabled="viewMode" class="prev-input-highlight" size="small" />
                            </div>
                            <table class="prev-table" style="margin-top:4px;">
                                <thead>
                                    <tr>
                                        <th>시작투입량(ml)</th><th>평가자</th>
                                        <th>중량</th><th>중량</th><th>중량</th>
                                        <th rowspan="3">특이사항</th>
                                    </tr>
                                    <tr>
                                        <th>원수공급량(1t)</th><th>용해농도</th>
                                        <th>#1농도(g)</th><th>#2농도(g)</th><th>#3농도(g)</th>
                                    </tr>
                                    <tr>
                                        <th></th><th></th>
                                        <th>#1제거율(%)</th><th>#2제거율(%)</th><th>#3제거율(%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td class="prev-empty"></td>
                                        <td class="prev-empty"></td>
                                        <td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td>
                                        <td class="prev-empty" rowspan="3" style="vertical-align:top;padding:2px;"></td>
                                    </tr>
                                    <tr>
                                        <td class="prev-empty"></td>
                                        <td class="prev-empty"></td>
                                        <td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td></td>
                                        <td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="prev-note-editable">
                                <a-input v-model="formData.templateTexts.chlorineNote" :disabled="viewMode" class="prev-input-highlight" size="small" />
                            </div>
                        </div>

                        <!-- 오후 -->
                        <div style="flex:1;">
                            <div class="prev-subsection-title">오후</div>
                            <div class="prev-note-editable">
                                <span class="prev-note-readonly">{{ formData.templateTexts.turbidityNote }}</span>
                            </div>
                            <table class="prev-table">
                                <thead>
                                    <tr><th>모델</th><th>평가자</th><th>#1탁도</th><th>#1유량</th><th>#2탁도</th><th>#2유량</th><th>#3탁도</th><th>#3유량</th><th>특이사항</th></tr>
                                </thead>
                                <tbody>
                                    <tr><td class="prev-empty" colspan="9" style="height:28px;"></td></tr>
                                    <tr>
                                        <td class="prev-label" colspan="2">원수탁도 / 시간</td>
                                        <td class="prev-empty" colspan="7"></td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="prev-note-editable" style="margin-top:6px;">
                                <span class="prev-note-readonly">{{ formData.templateTexts.concentrationFormula }}</span>
                            </div>
                            <table class="prev-table" style="margin-top:4px;">
                                <thead>
                                    <tr>
                                        <th>시작투입량(ml)</th><th>평가자</th>
                                        <th>중량</th><th>중량</th><th>중량</th>
                                        <th rowspan="3">특이사항</th>
                                    </tr>
                                    <tr>
                                        <th>원수공급량(1t)</th><th>용해농도</th>
                                        <th>#1농도(g)</th><th>#2농도(g)</th><th>#3농도(g)</th>
                                    </tr>
                                    <tr>
                                        <th></th><th></th>
                                        <th>#1제거율(%)</th><th>#2제거율(%)</th><th>#3제거율(%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td class="prev-empty"></td>
                                        <td class="prev-empty"></td>
                                        <td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td>
                                        <td class="prev-empty" rowspan="3" style="vertical-align:top;padding:2px;"></td>
                                    </tr>
                                    <tr>
                                        <td class="prev-empty"></td>
                                        <td class="prev-empty"></td>
                                        <td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td></td>
                                        <td class="prev-empty"></td><td class="prev-empty"></td><td class="prev-empty"></td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="prev-note-editable">
                                <span class="prev-note-readonly">{{ formData.templateTexts.chlorineNote }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- 하단 -->
                    <table class="prev-table" style="margin-top:12px;width:100%;">
                        <tbody>
                            <tr>
                                <td class="prev-label" style="width:100px;">유실 내용</td>
                                <td class="prev-empty" style="width:45%;"></td>
                                <td class="prev-label" style="width:90px;">평가 등당</td>
                                <td class="prev-empty">g</td>
                                <td class="prev-label" style="width:90px;">작업 등당</td>
                                <td class="prev-empty">kg</td>
                            </tr>
                        </tbody>
                    </table>
                </a-card>

                <a-divider />
                <div style="text-align: center;">
                    <a-button @click="handleModalCancel" style="margin-right: 8px;">{{ viewMode ? '닫기' : '취소' }}</a-button>
                    <template v-if="!viewMode">
                        <a-button type="default" @click="handleSaveTemp" style="margin-right: 8px;">임시저장</a-button>
                        <a-button type="primary" @click="handleSaveComplete">저장완료</a-button>
                    </template>
                </div>
            </a-form>
        </a-modal>
    </div>
</template>

<script>
import PageHeader from "../components/PageHeader.vue";

const columns = [
    { title: '상태', dataIndex: 'tag', key: 'tag', scopedSlots: { customRender: 'tags' }, width: 100 },
    { title: '템플릿명', dataIndex: 'name', key: 'name', scopedSlots: { customRender: 'name' } },
    { title: '라인명', dataIndex: 'lineName', key: 'lineName' },
    { title: '생산모델', dataIndex: 'productModel', key: 'productModel' },
    { title: '최종수정일', dataIndex: 'updated', key: 'updated' },
    { title: '관리', key: 'action', scopedSlots: { customRender: 'action' }, width: 320 },
];

export default {
    name: 'FilterWorkRegister',
    components: {
        PageHeader,
    },
    data() {
        return {
            flagSpinner: false,
            filterState: '모두',
            filterCS: '',
            validRate: 100,
            columns,
            data: [],
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}개`
            },
            isCreateModalVisible: false,
            editingSeq: null,
            viewMode: false,
            copyMode: false,
            productList: [],
            form: this.$form.createForm(this),
            formData: {
                basicInfo: {
                    templateName: '',
                    lineName: '',
                    writer: '',
                    productSeq: null,
                    dateTime: ''
                },
                templateTexts: {
                    managerInput: '',
                    turbidityNote: '10NTU 이하 유량:15~22',
                    concentrationFormula: '(원수농도-측정농도)/원수 농도*100',
                    chlorineNote: '염소 제거율 : 85% 이상'
                }
            }
        };
    },
    mounted() {
        this.fetchTemplateList();
        this.fetchProductList();
    },
    computed: {
        modalTitle() {
            if (this.viewMode) return '일일 작업일지 템플릿 상세보기';
            if (this.copyMode) return '일일 작업일지 템플릿 복사생성';
            if (this.editingSeq) return '일일 작업일지 템플릿 수정';
            return '일일 작업일지 템플릿 생성';
        },
    },
    methods: {
        fetchTemplateList() {
            this.flagSpinner = true;
            const page = this.pagination.current - 1;
            const size = this.pagination.pageSize;
            let url = `${this.$store.state.nodeApiUrl}/filter/work/template/list?size=${size}&page=${page}`;
            if (this.filterState === '등록중') url += '&state=FW_A';
            else if (this.filterState === '등록완료') url += '&state=FW_B';
            if (this.filterCS) url += `&name=${encodeURIComponent(this.filterCS)}`;
            
            this.$http.get(url).then((response) => {
                if (response.status === 200 && response.data.code === 'success') {
                    this.pagination.total = response.data.data.totalElements || 0;
                    this.data = response.data.data.content.map(item => ({
                        key: item.productCsSeq,
                        tag: item.state === 'FW_A' ? '등록중' : '등록완료',
                        name: item.name,
                        lineName: item.line,
                        productModel: item.productModel || '-',
                        updated: item.updated
                    }));
                }
                this.flagSpinner = false;
            }).catch((error) => { console.error(error); this.flagSpinner = false; });
        },
        changeFilterState(state) {
            this.filterState = state;
            this.filterCSList();
        },
        filterCSList() {
            this.pagination.current = 1;
            this.fetchTemplateList();
        },
        resetCSList() {
            this.filterCS = '';
            this.filterState = '모두';
            this.pagination.current = 1;
            this.fetchTemplateList();
        },
        handleTableChange(pagination) {
            this.pagination.current = pagination.current;
            this.pagination.pageSize = pagination.pageSize;
            this.fetchTemplateList();
        },
        showCreateModal() {
            this.viewMode = false;
            this.copyMode = false;
            this.isCreateModalVisible = true;
            this.resetFormData();
            this.formData.basicInfo.writer = sessionStorage.getItem('user_name') || '';
        },
        handleModalCancel() {
            this.isCreateModalVisible = false;
            this.viewMode = false;
            this.copyMode = false;
            this.resetFormData();
        },
        resetFormData() {
            this.editingSeq = null;
            this.copyMode = false;
            this.formData = {
                basicInfo: {
                    templateName: '',
                    lineName: '',
                    writer: '',
                    productSeq: null,
                    dateTime: ''
                },
                templateTexts: {
                    managerInput: '',
                    turbidityNote: '10NTU 이하 유량:15~22',
                    concentrationFormula: '(원수농도-측정농도)/원수 농도*100',
                    chlorineNote: '염소 제거율 : 85% 이상'
                }
            };
        },
        fetchProductList() {
            this.$http.get(`${this.$store.state.nodeApiUrl}/product/list?size=1000`)
                .then(response => {
                    if (response.status === 200 && response.data.code === 'success') {
                        this.productList = response.data.data.content;
                    }
                })
                .catch(err => console.error(err));
        },
        filterProductOption(input, option) {
            const label = (option.componentOptions.propsData.label || '').toLowerCase();
            return label.includes(input.toLowerCase());
        },
        loadTemplateIntoForm(data, { viewMode = false, editingSeq = null, copyMode = false } = {}) {
            this.viewMode = viewMode;
            this.copyMode = copyMode;
            this.editingSeq = editingSeq;

            this.formData.basicInfo.templateName = copyMode ? '' : (data.name || '');
            this.formData.basicInfo.lineName = data.line || data.lineName || '';
            this.formData.basicInfo.writer = copyMode
                ? (sessionStorage.getItem('user_name') || '')
                : (data.manager || data.writer || '');
            this.formData.basicInfo.productSeq = data.productSeq || null;

            const tt = data.templateTexts || data.appearance || {};
            this.formData.templateTexts = {
                managerInput: tt.managerInput || '',
                turbidityNote: tt.turbidityNote || '10NTU 이하 유량:15~22',
                concentrationFormula: tt.concentrationFormula || '(원수농도-측정농도)/원수 농도*100',
                chlorineNote: tt.chlorineNote || '염소 제거율 : 85% 이상'
            };
        },
        fetchTemplateItem(key, onSuccess) {
            this.$http.get(`${this.$store.state.nodeApiUrl}/filter/work/template/item/${key}`)
                .then((response) => {
                    if (response.status === 200 && response.data.code === 'success') {
                        onSuccess(response.data.data);
                    } else {
                        this.$message.error('데이터 조회에 실패했습니다.');
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.$message.error('데이터 조회에 실패했습니다.');
                });
        },
        handleSaveTemp() {
            this.saveTemplate('FW_A');
        },
        handleSaveComplete() {
            this.saveTemplate('FW_B');
        },
        checkDuplicateTemplateName(name) {
            return new Promise((resolve) => {
                const url = `${this.$store.state.nodeApiUrl}/filter/work/template/list?size=1000&page=0&name=${encodeURIComponent(name)}`;
                this.$http.get(url)
                    .then((response) => {
                        if (response.status === 200 && response.data.code === 'success') {
                            const items = response.data.data.content || [];
                            const isDuplicate = items.some(item => {
                                if (!this.copyMode && this.editingSeq && item.productCsSeq === this.editingSeq) {
                                    return false;
                                }
                                return item.name === name;
                            });
                            resolve(isDuplicate);
                        } else {
                            resolve(false);
                        }
                    })
                    .catch(() => resolve(false));
            });
        },
        saveTemplate(state) {
            const templateName = this.formData.basicInfo.templateName || '작업일지 템플릿';
            this.flagSpinner = true;
            this.checkDuplicateTemplateName(templateName).then((isDuplicate) => {
                if (isDuplicate) {
                    this.$message.error(`'${templateName}' 템플릿명이 이미 존재합니다. 다른 이름을 입력해주세요.`);
                    this.flagSpinner = false;
                    return;
                }
                this.persistTemplate(state);
            });
        },
        persistTemplate(state) {
            const selectedProduct = this.productList.find(p => p.productSeq === this.formData.basicInfo.productSeq);
            const postData = {
                name: this.formData.basicInfo.templateName || '작업일지 템플릿',
                state: state,
                lineName: this.formData.basicInfo.lineName,
                writer: this.formData.basicInfo.writer,
                productSeq: this.formData.basicInfo.productSeq,
                productModel: selectedProduct ? selectedProduct.productModel : '',
                templateTexts: this.formData.templateTexts,
                creator: sessionStorage.getItem('user_name') || 'system',
                updater: sessionStorage.getItem('user_name') || 'system'
            };

            const isEdit = !!this.editingSeq;
            const url = isEdit
                ? `${this.$store.state.nodeApiUrl}/filter/work/template/update/${this.editingSeq}`
                : `${this.$store.state.nodeApiUrl}/filter/work/template/create`;
            const request = isEdit ? this.$http.put(url, postData) : this.$http.post(url, postData);

            request
                .then((response) => {
                    if (response.status === 200 && response.data.code === 'success') {
                        this.$message.success(state === 'FW_A' ? '임시저장되었습니다.' : '저장되었습니다.');
                        this.handleModalCancel();
                        this.fetchTemplateList();
                    } else {
                        this.$message.error('저장에 실패했습니다: ' + (response.data.message || '알 수 없는 오류'));
                    }
                }).catch((error) => { console.error(error); this.$message.error('저장에 실패했습니다.'); });
        },
        handlerToEdit(key) {
            this.fetchTemplateItem(key, (data) => {
                this.loadTemplateIntoForm(data, { editingSeq: key });
                this.isCreateModalVisible = true;
            });
        },
        handlerToCopy(key) {
            this.fetchTemplateItem(key, (data) => {
                this.loadTemplateIntoForm(data, { copyMode: true, editingSeq: null });
                this.isCreateModalVisible = true;
            });
        },
        handlerToDelete(key) {
            this.$confirm({
                title: '삭제 확인',
                content: '정말 삭제하시겠습니까?',
                onOk: () => {
                    this.$http.delete(`${this.$store.state.nodeApiUrl}/filter/work/template/delete/${key}`)
                        .then((response) => {
                            if (response.status === 200 && response.data.code === 'success') {
                                this.$message.success('삭제되었습니다.');
                                this.fetchTemplateList();
                            }
                        }).catch((error) => { console.error(error); this.$message.error('삭제에 실패했습니다.'); });
                }
            });
        },
        handlerToView(key) {
            this.fetchTemplateItem(key, (data) => {
                this.loadTemplateIntoForm(data, { viewMode: true, editingSeq: null });
                this.isCreateModalVisible = true;
            });
        }
    }
};
</script>

<style scoped>
.state-btn { font-weight: bold; padding: 2px 8px; }
.state-btn.red { background-color: #ff4d4f; color: white; }
.state-btn.green { background-color: #52c41a; color: white; }

/* 양식 미리보기 */
.prev-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 2px solid #333;
}
.prev-title {
    font-size: 16px;
    font-weight: bold;
    color: #c0392b;
    display: flex;
    align-items: center;
    gap: 4px;
}
.prev-fixed-text { font-size: 12px; color: #666; }
.prev-section-title {
    font-size: 13px;
    font-weight: bold;
    background: #e8f4fd;
    padding: 3px 8px;
    margin: 10px 0 5px 0;
    border-left: 4px solid #2980b9;
}
.prev-subsection-title {
    font-size: 11px;
    font-weight: bold;
    color: #555;
    margin-bottom: 3px;
}
.prev-note-editable { margin: 3px 0; }
.prev-note-readonly {
    font-size: 11px;
    color: #c0392b;
    font-weight: bold;
}
.prev-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    margin-bottom: 3px;
}
.prev-table th, .prev-table td {
    border: 1px solid #bbb;
    padding: 3px 5px;
    text-align: center;
    vertical-align: middle;
}
.prev-table th { background: #f0f4f8; font-weight: bold; font-size: 10px; }
.prev-table td.prev-label { background: #fafafa; font-weight: bold; font-size: 10px; white-space: nowrap; }
.prev-table td.prev-empty { background: #fff; min-width: 40px; height: 22px; color: #aaa; font-size: 10px; }
.prev-empty-area {
    border: 1px solid #bbb;
    background: #fff;
    height: 50px;
    margin-bottom: 4px;
}
/* 관리자가 입력하는 필드 강조 (노란색) */
.prev-input-highlight {
    background-color: #fffbe6 !important;
    border-color: #faad14 !important;
}
.prev-input-inline {
    display: inline-block;
}
</style>
